import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import toast from 'react-hot-toast'
import { Eye, EyeOff, Gift, PackageCheck, Zap, LogOut } from 'lucide-react'
import { useCustomerAuth } from '../../contexts/CustomerAuthContext'
import { useTenant } from '../../contexts/TenantContext'
import { useCurrency, price } from './templates/shared'
import { t as tr, locale } from '../../i18n'

// Customer sign in / create account and profile. Styled with the storefront
// skin classes, so it takes the look of the store's template.
function AuthForm() {
  const { login, register } = useCustomerAuth()
  const tenant = useTenant()
  const [mode, setMode] = useState('login')
  const [phone, setPhone] = useState('')
  const [name, setName] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const creating = mode === 'register'
  const storeName = tenant.name || 'our store'

  const submit = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      if (creating) {
        await register(phone.trim(), name.trim(), password)
        toast.success(tr("Welcome! Your account is ready."))
      } else {
        await login(phone.trim(), password)
      }
    } catch (err) {
      setError(tr(err.response?.data?.error || 'Something went wrong. Please try again.'))
    } finally {
      setLoading(false)
    }
  }

  const perks = [
    [PackageCheck, 'Track every order', 'See the status of your orders live.'],
    [Gift, 'Earn rewards', 'Collect points each time you order.'],
    [Zap, 'Faster checkout', 'Your details are ready next time.'],
  ]

  return (
    <div className="sk-page grid md:grid-cols-2 sk-card p-0 overflow-hidden max-w-4xl">
      <section className="p-8 md:p-10 flex flex-col justify-between gap-10" style={{ background: 'var(--theme-secondary)' }}>
        <div>
          <p className="eyebrow">{storeName}</p>
          <h1 className="sk-heading">{creating ? tr("Join us.") : tr("Welcome back.")}</h1>
          <p className="sk-muted mt-4 max-w-sm">{creating ? tr("Create your {storeName} account in a few seconds.", { storeName: storeName }) : tr("Sign in with the phone number you used to register.")}</p>
        </div>
        <ul className="space-y-5">
          {perks.map(([Icon, title, text]) => (
            <li key={title} className="flex gap-4 items-start">
              <span className="w-10 h-10 grid place-items-center flex-shrink-0 rounded-full" style={{ background: 'var(--theme-primary)', color: 'var(--sk-on-primary)' }}><Icon size={18} aria-hidden="true" /></span>
              <div><p className="font-semibold">{tr(title)}</p><p className="text-sm sk-muted">{tr(text)}</p></div>
            </li>
          ))}
        </ul>
      </section>

      <section className="p-8 md:p-10">
        <div className="sk-tabs mb-8" role="tablist" aria-label={tr("Account")}>
          <button role="tab" aria-selected={!creating} onClick={() => { setMode('login'); setError('') }}>{tr("Sign in")}</button>
          <button role="tab" aria-selected={creating} onClick={() => { setMode('register'); setError('') }}>{tr("Create account")}</button>
        </div>
        <form onSubmit={submit} className="space-y-5" noValidate={false}>
          {creating && (
            <div>
              <label className="flow-label" htmlFor="acct-name">{tr("Your name")}</label>
              <input id="acct-name" className="flow-input" autoComplete="name" maxLength={100} value={name} onChange={e => setName(e.target.value)} placeholder={tr("So we know what to call you")} />
            </div>
          )}
          <div>
            <label className="flow-label" htmlFor="acct-phone">{tr("Phone number")}</label>
            <input id="acct-phone" className="flow-input" type="tel" required autoComplete="tel" maxLength={30} value={phone} onChange={e => setPhone(e.target.value)} placeholder="+212 600 000 000" />
          </div>
          <div>
            <label className="flow-label" htmlFor="acct-password">{tr("Password")}</label>
            <div className="relative">
              <input id="acct-password" className="flow-input pe-12" type={showPassword ? 'text' : 'password'} required minLength={6}
                autoComplete={creating ? 'new-password' : 'current-password'} value={password} onChange={e => setPassword(e.target.value)} />
              <button type="button" onClick={() => setShowPassword(s => !s)} className="absolute inset-y-0 end-0 px-4 sk-muted" aria-label={showPassword ? tr("Hide password") : tr("Show password")}>
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {creating && <p className="text-xs sk-muted mt-2">{tr("At least 6 characters.")}</p>}
          </div>
          {error && <p role="alert" className="rounded-lg bg-red-50 text-red-700 text-sm px-4 py-3">{error}</p>}
          <button type="submit" disabled={loading} className="sk-btn w-full">
            {loading ? tr("Please wait…") : creating ? tr("Create my account") : tr("Sign in")}
          </button>
        </form>
        <p className="text-sm sk-muted text-center mt-6">
          {creating ? tr("Already have an account? ") : tr("New here? ")}
          <button className="underline font-medium" style={{ color: 'var(--theme-text)' }} onClick={() => { setMode(creating ? 'login' : 'register'); setError('') }}>
            {creating ? tr("Sign in") : tr("Create an account")}
          </button>
        </p>
      </section>
    </div>
  )
}

function Profile() {
  const { customer, logout, client } = useCustomerAuth()
  const currency = useCurrency()
  const { search } = useLocation()
  const [tab, setTab] = useState('rewards')
  const [rewards, setRewards] = useState([])
  const [orders, setOrders] = useState([])
  const [ledger, setLedger] = useState([])

  useEffect(() => {
    client.get('/api/customer/rewards').then(r => setRewards(r.data.rewards)).catch(() => {})
    client.get('/api/customer/orders').then(r => setOrders(r.data.orders)).catch(() => {})
    client.get('/api/customer/ledger').then(r => setLedger(r.data.ledger)).catch(() => {})
  }, [])

  const empty = text => <p className="text-sm sk-muted text-center py-10">{text}</p>
  const row = 'sk-card p-4 flex justify-between items-center gap-4'

  return (
    <div className="sk-page max-w-2xl">
      <section className="sk-card p-8" style={{ background: 'var(--theme-primary)', color: 'var(--sk-on-primary)', borderColor: 'transparent' }}>
        <p className="text-sm opacity-80">{tr("Hello,")} {customer.name || customer.phone}</p>
        <p className="sk-heading mt-2">{customer.points_balance} {tr("pts")}</p>
        <p className="text-sm opacity-75 mt-2">{customer.lifetime_points} {tr("earned all-time ·")} {customer.visits} {tr("visits")}</p>
      </section>

      <div className="sk-tabs my-6" role="tablist" aria-label={tr("Your account")}>
        {['rewards', 'orders', 'history'].map(t => (
          <button key={t} role="tab" aria-selected={tab === t} onClick={() => setTab(t)}>{t}</button>
        ))}
      </div>

      {tab === 'rewards' && <div className="space-y-3">
        {rewards.length === 0 && empty('No rewards set up yet.')}
        {rewards.map(r => (
          <div key={r.id} className={row} style={r.affordable ? { borderColor: 'var(--theme-primary)' } : undefined}>
            <div><p className="font-medium">{r.name}</p><p className="text-xs sk-muted">{r.points_cost} {tr("pts")}</p></div>
            {r.affordable ? <span className="sk-chip">{tr("Ready to redeem")}</span> : <span className="text-xs sk-muted">{r.pointsShort} {tr("pts to go")}</span>}
          </div>
        ))}
      </div>}

      {tab === 'orders' && <div className="space-y-3">
        {orders.length === 0 && empty('No orders yet.')}
        {orders.map(o => (
          <div key={o.id} className={row}>
            <div className="min-w-0">
              <p className="font-medium truncate">{o.order_code}</p>
              <p className="text-xs sk-muted">{new Date(o.created_at).toLocaleDateString(locale())} · {o.status}</p>
            </div>
            <div className="text-end">
              <p className="font-semibold">{price(o.total, currency)}</p>
              {o.pin && <Link className="text-xs underline" to={`/order-status?pin=${o.pin}${search ? `&${search.slice(1)}` : ''}`}>{tr("Track")}</Link>}
            </div>
          </div>
        ))}
      </div>}

      {tab === 'history' && <div className="space-y-3">
        {ledger.length === 0 && empty('No points activity yet.')}
        {ledger.map(l => (
          <div key={l.id} className={row}>
            <div><p className="text-sm">{l.note}</p><p className="text-xs sk-muted">{new Date(l.created_at).toLocaleDateString(locale())}</p></div>
            <p className={`font-semibold ${l.points > 0 ? 'text-emerald-600' : 'text-red-500'}`}>{l.points > 0 ? '+' : ''}{l.points}</p>
          </div>
        ))}
      </div>}

      <button onClick={logout} className="sk-btn-ghost w-full mt-8"><LogOut size={16} /> {tr("Sign out")}</button>
    </div>
  )
}

export default function AccountPage() {
  const { customer, loading } = useCustomerAuth()
  if (loading) return <div className="text-center py-20 sk-muted">{tr("Loading...")}</div>
  return customer ? <Profile /> : <AuthForm />
}
